import { Box, Collapse } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import CustomButton from 'components/buttons/CustomButton/CustomButton';
import CustomFormTextInput from 'components/inputs/CustomFormTextInput/CustomFormTextInput';

import { PatientsFiltersDto } from 'types/dto/PatientsFiltersDto';
import CustomFormSelect from 'components/inputs/CustomFormSelect/CustomFormSelect';
import { filterPatientsFilterList, filterPatientsSearchByList, sortByOptionsList } from 'common/constants';
import CustomDateRangePicker from 'components/inputs/CustomDateRangePicker/CustomDateRangePicker';
import dayjs from 'dayjs';
import { DateRangeIcon } from '@mui/x-date-pickers';
import SearchIcon from 'icons/SearchIcon';
import { ECaseFilter } from 'enum/ECaseFilter';
import { getQuickFilterDateParams } from 'common/utils';

interface Props {
  open: boolean;
  initialValues: PatientsFiltersDto;
  loading: boolean;
  onApply: (filters: PatientsFiltersDto) => void;
  onReset: () => void;
}

export default function PatientsFilters({
  open,
  initialValues,
  loading,
  onApply,
  onReset,
}: Props) {

  type FiltersFormValues = PatientsFiltersDto & {
    dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  };

  const methods = useForm<FiltersFormValues>({
    defaultValues: {
      ...initialValues,
      dateRange: [
        initialValues.start_date
          ? dayjs(initialValues.start_date)
          : null,
        initialValues.end_date
          ? dayjs(initialValues.end_date)
          : null,
      ],
    },
  });


  const { handleSubmit, reset, control, watch, setValue } = methods;

  const watchedFilter = watch('filter');
  const watchedDateRange = watch('dateRange');

  // Quick filter (Today/This Week/etc.) and the manual date range are
  // mutually exclusive - picking one clears the other, since combining
  // both would silently intersect two different date restrictions.
  useEffect(() => {
    if (watchedFilter && watchedFilter !== ECaseFilter.ALL) {
      setValue('dateRange', [null, null]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFilter]);

  useEffect(() => {
    const [start, end] = watchedDateRange ?? [];
    if ((start || end) && watchedFilter !== ECaseFilter.ALL) {
      setValue('filter', ECaseFilter.ALL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedDateRange]);

  return (
    <Collapse in={open} timeout="auto" unmountOnExit>
      <Box
        sx={{
          px: 2,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={handleSubmit((values) => {
              const isQuickFilter = values.filter && values.filter !== ECaseFilter.ALL;

              if (isQuickFilter) {
                onApply({
                  filter: ECaseFilter.ALL,
                  search_by: values.search_by,
                  sort_by: values.sort_by,
                  value: values.value,
                  ...getQuickFilterDateParams(values.filter as ECaseFilter),
                });
                return;
              }

              const [start, end] = values.dateRange ?? [];
              onApply({
                filter: values.filter,
                search_by: values.search_by,
                sort_by: values.sort_by,
                value: values.value,
                start_date: start ? dayjs(start).format('YYYY-MM-DD') : undefined,
                end_date: end ? dayjs(end).format('YYYY-MM-DD') : undefined,
              });
            })}
            display="grid"
            gridTemplateColumns={{
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(2, 3fr)',
              lg: 'repeat(3, 2fr)',
            }}
            columnGap={2}
            rowGap={1}
            flex={1}
          >
            {/* Search */}
            <CustomFormTextInput
              name="value"
              placeholder='Search'
              floatingLabel='Search'
              register={methods.register}
              endIcon={<SearchIcon size={16} />}
            />

            {/* Serach by */}
            <CustomFormSelect
              name="search_by"
              placeholder='Search by'
              floatingLabel='Search by'
              options={filterPatientsSearchByList}
            />

            {/* Filter */}
            <CustomFormSelect
              name="filter"
              placeholder='Filter'
              floatingLabel='Filter'
              options={filterPatientsFilterList}
            />

            {/* Date range */}
            <CustomDateRangePicker
              name="dateRange"
              control={control}
              floatingLabel="Date range"
              placeholder="Date range"
              endIcon={<DateRangeIcon sx={{ fontSize: 20 }} />}
            />

            {/* Sort */}
            <CustomFormSelect
              name="sort_by"
              placeholder='Sort'
              floatingLabel='Sort'
              options={sortByOptionsList}
            />

            {/* Actions */}
            <Box
              display="flex"
              gap={1}
              alignItems="flex-end"
              justifyContent="flex-end"
              sx={{
                alignSelf: 'end',
              }}
            >
              <CustomButton
                fullWidth
                text="Reset"
                variant="outlined"
                height={40}
                disabled={loading}
                onClick={() => {
                  reset();
                  onReset();
                }}
              />
              <CustomButton
                fullWidth
                text="Search"
                type="submit"
                height={40}
                isLoading={loading}
              />
            </Box>
          </Box>
        </FormProvider>
      </Box>
    </Collapse>
  );
}
